/**
 * Builds a fully-qualified live-chat message ARI from a chat ARI and a bare
 * message UUID, in the format the backend uses on `LiveChatUserMessage.id`:
 *
 *   ari:cloud:communication::message/{orgUuid}/{messageUuid}
 *
 * The chat ARI is expected to look like:
 *
 *   ari:cloud:communication::chat/{orgUuid}/{chatUuid}
 *
 * The backend uses the `{orgUuid}` segment (the second-to-last path segment)
 * as the parent of the message ARI — NOT the `{chatUuid}` segment. Reproducing
 * that here lets the optimistic client-side message and the subscription echo
 * compare by exact-string ARI equality.
 *
 * @returns A message ARI string, or `null` if either input is missing or the
 *   chat ARI does not contain the expected `…/{orgUuid}/{chatUuid}` suffix.
 */
export const buildMessageAri = (
	chatAri: string | null | undefined,
	messageUuid: string | null | undefined,
): string | null => {
	if (!chatAri || !messageUuid) {
		return null;
	}
	const segments = chatAri.split('/');
	if (segments.length < 2) {
		return null;
	}
	const orgUuid = segments[segments.length - 2];
	if (!orgUuid) {
		return null;
	}
	return `ari:cloud:communication::message/${orgUuid}/${messageUuid}`;
};
